import _defineProperty from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React from 'react';
import { Box } from '@hub/box';
import { useToken, useTheme, css, mapRules } from '@hub/design-system-base';
import { H3 } from '@hub/heading';
import { Link } from '@hub/link';
import { Stack } from '@hub/stack';
import { Text } from '@hub/text';
import { IncludeDirectLogo } from '@hub/icon/src/with-direct-logo';
import trackClickEvents, { categories } from '../analytics/trackEvents';
import { CloudinaryImage, IMAGE_SIZES_DECK_PRIMARY } from '../cloudinary-image';
import { withIsAboveFoldProvider } from '../is-above-fold-provider';
import { ProductCard } from '../product-card';
import { AnalyticsListProvider } from '../analytics/analytics-list-context';
import { useAnalytics } from '../../analytics';
import { makeProductClickedForVariantGroup } from '../../analytics-segment/events/product-clicked';
import { AnalyticsImpressionTracker } from '../../analytics-observer';
import { Reel } from './reel';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const ProductBox = _ref => {
  var _sx$minWidth, _sx$maxWidth, _sx$flexBasis;

  let {
    product,
    sx = {},
    isFirst,
    isLast,
    backgroundColor,
    onClick
  } = _ref;
  const theme = useTheme();
  const [paddingSpace] = useToken('space', ['spacing-sm']); // We need to account for the spacing on the left/right of the first/last
  // items, which is used for the white border effect around the reel. If we
  // don't modify the minWidth, that extra padding (which isn't present on any
  // other items in the Reel) will result in the width of the image inside the
  // content box of <ProductCard> to be smaller (because image width + padding
  // of first/last items == image width of middle items).
  // Because sx.minWidth can be a responsive value, we have to map the rules
  // and set the value to a `calc` to ensure everything is type safe and
  // responsive safe.

  const adjustedFlexProperties = mapRules(css({
    minWidth: (_sx$minWidth = sx.minWidth) !== null && _sx$minWidth !== void 0 ? _sx$minWidth : 0,
    maxWidth: (_sx$maxWidth = sx.maxWidth) !== null && _sx$maxWidth !== void 0 ? _sx$maxWidth : 0,
    flexBasis: (_sx$flexBasis = sx.flexBasis) !== null && _sx$flexBasis !== void 0 ? _sx$flexBasis : 0
  })(theme), value => "calc(".concat(value, " + ").concat(paddingSpace, ")"));
  return /*#__PURE__*/_jsx(AnalyticsImpressionTracker, {
    child: ProductCard,
    sx: _objectSpread(_objectSpread(_objectSpread(_objectSpread({
      zIndex: 'elevatedContent',
      backgroundColor,
      flexGrow: 1
    }, sx), {}, {
      // To give a "border" effect, we put padding around each item, which
      // allows the background color of the containing Reel to show through.
      paddingY: 'spacing-sm'
    }, isFirst && _objectSpread({
      paddingLeft: 'spacing-sm'
    }, adjustedFlexProperties)), isLast && _objectSpread({
      paddingRight: 'spacing-sm'
    }, adjustedFlexProperties)), !isLast && !isFirst && {
      outlineStyle: 'solid',
      outlineColor: backgroundColor,
      outlineWidth: paddingSpace
    }),
    product: product,
    onClick: onClick,
    onCreateImpressionWhenView: product.viewItem,
    "data-id": "collection-deck_index_ProductBox_AnalyticsImpressionTracker"
  });
};

const ABOVE_FOLD_THRESHOLD = 3;

const ProductBoxes = _ref2 => {
  let {
    products,
    title,
    sx = {}
  } = _ref2;
  const {
    trackEvent
  } = useAnalytics();
  return /*#__PURE__*/_jsx(_Fragment, {
    children: products.map((product, index) => {
      const ProductBoxWithFold = withIsAboveFoldProvider(ProductBox, index < ABOVE_FOLD_THRESHOLD ? 'above' : 'below');
      return /*#__PURE__*/_jsx(ProductBoxWithFold, {
        product: product,
        isFirst: index === 0,
        isLast: index === products.length - 1,
        backgroundColor: "white",
        sx: sx,
        onClick: () => {
          trackEvent(makeProductClickedForVariantGroup(product));
          trackClickEvents.clicked(categories.PRODUCT_COLLECTION_DECK, "".concat(title, " | ").concat(product.title, " | P").concat(index + 1));
        },
        "data-id": "collection-deck_index_ProductBox_ProductBoxWithFold"
      }, product.variantGroupCode);
    })
  });
};

export const CollectionDeck = _ref3 => {
  let {
    title,
    text,
    url,
    image,
    products,
    as
  } = _ref3;
  const [reelPadding, productsMobileNegativeMarginTop, productsMobileNegativeMarginLeft, productsMobileNegativeMarginRight, productsNonMobileNegativeMaxMarginLeft, scrollPaddingInlineStart] = useToken('space', ['spacing-sm', 'spacing-md', 'spacing-md', 'spacing-md', 'spacing-lg', 'spacing-xl']);
  const [mobileBreakpoint, tabletBreakpoint] = useToken('breakpoints', ['sm', 'md']);
  const reelMarginOffsetDesktop = "max(-50%, calc(-".concat(productsNonMobileNegativeMaxMarginLeft, " - ").concat(reelPadding, "))"); // Convert the breakpoints into an absolute value so it can be used as the
  // divisor in a calc (at least one side of a division must be unitless)

  const tabletWidthRems = (parseFloat(tabletBreakpoint) - parseFloat(mobileBreakpoint)).toString(); // How far is the current viewport width into the tablet breakpoint, in
  // multiples of rems? NOTE: assumes 1rem = 16px

  const distanceIntoTabletRems = "calc(calc(100vw - ".concat(mobileBreakpoint, ") / 16)");
  const distanceIntoTabletInverseRatio = "calc(1px - calc(".concat(distanceIntoTabletRems, " / ").concat(tabletWidthRems, "))"); // How far should the Reel travel left over the course of the tablet
  // breakpoint?
  // Must be unitless, but interpreted as pixels

  const distanceForReelToTravelOnTabletSize = '100';
  const reelMarginOffsetTablet = "calc(".concat(reelMarginOffsetDesktop, " - calc(").concat(distanceForReelToTravelOnTabletSize, " * ").concat(distanceIntoTabletInverseRatio, "))");
  const CONTAINER_WIDTH = '100%';
  const MIN_LEFT_COLUMN_WIDTH = 'var(--chakra-sizes-size-lg)'; // Left column is either 42%, or a maximum of half the screen height to
  // support landscape phone / tablet sizes

  const MAX_LEFT_COLUMN_WIDTH = 'min(42%, 430px)';
  const MIN_RIGHT_COLUMN_WIDTH = "calc(".concat(CONTAINER_WIDTH, " - ").concat(MAX_LEFT_COLUMN_WIDTH, ")"); // prettier-ignore

  const rightColumnMaxWidth = 'calc(' + "".concat(CONTAINER_WIDTH) + ' - max(' + "".concat(MAX_LEFT_COLUMN_WIDTH, ",") + "".concat(MIN_LEFT_COLUMN_WIDTH) + ')' + ')';
  return /*#__PURE__*/_jsxs(Box, {
    sx: {
      display: ['block', 'flow-root'],
      margin: '0 auto'
    },
    as: as,
    "data-id": "collection-deck_index_ProductBox_Box",
    children: [/*#__PURE__*/_jsxs(Stack, {
      sx: {
        float: [null, 'right'],
        minWidth: MIN_RIGHT_COLUMN_WIDTH,
        maxWidth: [null, rightColumnMaxWidth],
        paddingLeft: [null, 'spacing-md'],
        marginBottom: ['spacing-sm', null]
      },
      gap: "spacing-xs",
      "data-id": "collection-deck_index_ProductBox_Box_Stack",
      children: [/*#__PURE__*/_jsxs(Stack, {
        gap: "spacing-none",
        "data-id": "collection-deck_index_ProductBox_Stack_Stack_4",
        children: [/*#__PURE__*/_jsx(H3, {
          align: ['center', 'left', 'left'],
          "data-id": "collection-deck_index_ProductBox_Stack_H3",
          children: /*#__PURE__*/_jsx(IncludeDirectLogo, {
            title: title,
            "data-id": "collection-deck_index_ProductBox_H3_IncludeDirectLogo"
          })
        }), /*#__PURE__*/_jsx(Text, {
          color: "quartz",
          noOfLines: 2,
          align: ['center', 'left', 'left'],
          "data-id": "collection-deck_index_ProductBox_Stack_Text",
          children: text
        })]
      }), /*#__PURE__*/_jsx(Text, {
        align: ['center', 'left', 'left'],
        "data-id": "collection-deck_index_ProductBox_Stack_Text_8",
        children: /*#__PURE__*/_jsx(Link, {
          hasUnderline: true,
          href: url,
          onClick: () => trackClickEvents.clicked(categories.PRODUCT_COLLECTION_DECK, "".concat(title, " | Shop Now")),
          "data-id": "collection-deck_index_ProductBox_Text_Link",
          children: "Shop now"
        })
      })]
    }), /*#__PURE__*/_jsx(Link, {
      href: url,
      onClick: () => trackClickEvents.clicked(categories.PRODUCT_COLLECTION_DECK, title),
      "data-id": "collection-deck_index_ProductBox_Box_Link_10",
      children: /*#__PURE__*/_jsx(CloudinaryImage, {
        imageSetOrImage: image,
        ratio: "1",
        sx: {
          float: [null, 'left'],
          minWidth: 'size-lg',
          maxWidth: [null, MAX_LEFT_COLUMN_WIDTH],
          width: 'size-full'
        },
        sizes: IMAGE_SIZES_DECK_PRIMARY,
        "data-id": "collection-deck_index_ProductBox_Link_CloudinaryImage"
      })
    }), /*#__PURE__*/_jsx(Box, {
      sx: {
        zIndex: 'elevatedContent',
        float: [null, 'right'],
        minWidth: MIN_RIGHT_COLUMN_WIDTH,
        maxWidth: [null, rightColumnMaxWidth],
        width: 'size-full'
      },
      "data-id": "collection-deck_index_ProductBox_Box_Box",
      children: /*#__PURE__*/_jsx(AnalyticsListProvider, {
        value: {
          list: title,
          positions: products.reduce((acc, product, index) => {
            acc[product.sku] = index + 1;
            return acc;
          }, {})
        },
        "data-id": "collection-deck_index_ProductBox_Box_AnalyticsListProvider",
        children: /*#__PURE__*/_jsx(Reel, {
          snap: true,
          minWidth: "size-lg",
          maxWidth: "size-xl",
          shouldWrapChildren: false,
          gap: "spacing-sm",
          sx: {
            // Ensure background sits above adjacent image
            position: 'relative',
            backgroundColor: 'white',
            // Only add background to the content, not the padding
            backgroundClip: 'content-box',
            paddingLeft: ['spacing-xl', 'spacing-0'],
            scrollPaddingInlineStart: [scrollPaddingInlineStart, null, 'initial'],
            marginTop: ["-".concat(productsMobileNegativeMarginTop), 0],
            marginLeft: ["-".concat(productsMobileNegativeMarginLeft), reelMarginOffsetTablet, reelMarginOffsetDesktop],
            marginRight: ["-".concat(productsMobileNegativeMarginRight), "-".concat(productsMobileNegativeMarginRight), 'initial'],
            display: 'flex'
          },
          "data-id": "collection-deck_index_ProductBox_AnalyticsListProvider_Reel",
          children: /*#__PURE__*/_jsx(ProductBoxes, {
            products: products,
            title: title,
            "data-id": "collection-deck_index_ProductBox_Reel_ProductBoxes"
          })
        })
      })
    })]
  });
};